/**
 * Typed client for the mini-TRA API.
 *
 * Note what `Option` does NOT have: a score. The server sends an id and a label
 * and nothing else, and the client returns the id it was given. The scoring
 * model is never present in this process, so it can't leak from it — that's the
 * guarantee in docs/IP_PROTECTION.md, expressed here as a type.
 *
 * Paths are relative. In production the static site and the API are served from
 * the same origin; in dev Vite proxies /api. Same code either way.
 */

export interface Option {
  id: string;
  label: string;
}

export interface Question {
  id: string;
  position: number;
  category: string;
  text: string;
  options: Option[];
  selected_option_id: string | null;
}

export interface StartSessionResponse {
  session_token: string;
  /** The instrument actually started — may differ from what was asked for. */
  instrument: string;
  instrument_label: string;
  template_version: number;
  total_questions: number;
  question: Question;
}

/** One assessment on offer. Carries no scoring information, by construction. */
export interface Instrument {
  slug: string;
  label: string;
  blurb: string;
  total_questions: number;
  total_categories: number;
  /** Reachable only by an explicit ?test= link; the picker doesn't offer it. */
  unlisted: boolean;
}

export interface AnswerResponse {
  answered_count: number;
  total_questions: number;
  next_question: Question | null;
}

export interface CategoryResult {
  category: string;
  band: string;
  score: number;
}

export interface SubmitResponse {
  uuid: string;
  instrument: string;
  instrument_label: string;
  band: string;
  overall_score: number;
  categories: CategoryResult[];
  report_email_queued: boolean;
}

export interface Contact {
  name: string;
  email: string;
  company: string;
  phone: string;
  /** Honeypot. Hidden from real users; bots fill it in. */
  website: string;
  elapsed_ms: number;
}

/** A failed request, with enough detail for the UI to say something useful. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly fieldErrors: Record<string, string> = {},
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** Retrying is worth offering for network blips and server faults, not for 4xx. */
  get isRetryable(): boolean {
    return this.status === 0 || this.status >= 500;
  }

  /** The session expired or was never valid — the run has to start over. */
  get isSessionGone(): boolean {
    return this.status === 404;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    });
  } catch {
    // Offline, DNS failure, connection reset — no HTTP status exists.
    throw new ApiError("We couldn't reach the server. Check your connection.", 0);
  }

  if (response.status === 204) return undefined as T;

  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    /* some error responses have no body */
  }

  if (!response.ok) {
    const problem = (body ?? {}) as {
      title?: string;
      detail?: string;
      errors?: { field: string; message: string }[];
      // FastAPI's own validation shape, which differs from our RFC 9457 problems
      _fastapi?: unknown;
    };

    const fieldErrors: Record<string, string> = {};
    for (const e of problem.errors ?? []) fieldErrors[e.field] = e.message;

    // FastAPI returns 422 as {detail: [{loc, msg, ...}]} for schema failures
    const rawDetail = (body as { detail?: unknown })?.detail;
    if (Array.isArray(rawDetail)) {
      for (const item of rawDetail as { loc?: unknown[]; msg?: string }[]) {
        const field = String(item.loc?.[item.loc.length - 1] ?? "");
        if (field) fieldErrors[field] = item.msg ?? "Invalid value";
      }
    }

    throw new ApiError(
      problem.title ??
        (typeof rawDetail === "string" ? rawDetail : null) ??
        "Something went wrong.",
      response.status,
      fieldErrors,
    );
  }

  return body as T;
}

/** Campaign tags forwarded by the Wix embed snippet, read off our own URL. */
function readUtm(): Record<string, string> {
  const utm: Record<string, string> = {};
  const params = new URLSearchParams(window.location.search);
  params.forEach((value, key) => {
    if (/^(utm_[a-z_]+|ref|gclid|fbclid|msclkid)$/i.test(key) && value) {
      utm[key] = value.slice(0, 120);
    }
  });
  return utm;
}

export interface SolvedChallenge {
  challenge: string;
  issued_at: number;
  signature: string;
  solution: string;
}

/**
 * Which assessment this embed runs, read off our own URL as `?test=`.
 *
 * Kim installs one snippet per Wix page and sets this in it, so adding an
 * assessment to the site is a URL change rather than a deploy. An unrecognised
 * value is deliberately not validated here — the server resolves an unknown
 * slug to the default assessment, which is the right outcome for a typo in a
 * marketing URL.
 */
export function readInstrument(): string {
  const params = new URLSearchParams(window.location.search);
  return (params.get("test") ?? params.get("instrument") ?? "").slice(0, 40);
}

/** What the picker offers. Empty means "only one assessment is installed". */
export function listInstruments(): Promise<Instrument[]> {
  return request<Instrument[]>("/api/mini/instruments");
}

/** One instrument by slug — including an unlisted one reached via ?test=. */
export function describeInstrument(slug: string): Promise<Instrument> {
  return request<Instrument>(`/api/mini/instruments/${encodeURIComponent(slug)}`);
}

export function startSession(
  pow: SolvedChallenge,
  instrument?: string,
): Promise<StartSessionResponse> {
  return request<StartSessionResponse>("/api/mini/sessions", {
    method: "POST",
    body: JSON.stringify({
      utm: readUtm(),
      referrer: document.referrer.slice(0, 500),
      instrument: instrument ?? readInstrument(),
      pow_challenge: pow.challenge,
      pow_issued_at: pow.issued_at,
      pow_signature: pow.signature,
      pow_solution: pow.solution,
    }),
  });
}

export function answerQuestion(
  token: string,
  questionId: string,
  optionId: string,
): Promise<AnswerResponse> {
  return request<AnswerResponse>(`/api/mini/sessions/${encodeURIComponent(token)}/answers`, {
    method: "POST",
    body: JSON.stringify({ question_id: questionId, option_id: optionId }),
  });
}

/** Fetch one question by position — what Back calls, so a prior answer is restored. */
export function getQuestion(token: string, position: number): Promise<Question> {
  return request<Question>(
    `/api/mini/sessions/${encodeURIComponent(token)}/questions/${position}`,
  );
}

export function submitAssessment(token: string, contact: Contact): Promise<SubmitResponse> {
  return request<SubmitResponse>(`/api/mini/sessions/${encodeURIComponent(token)}/submit`, {
    method: "POST",
    body: JSON.stringify(contact),
  });
}

/** Fire-and-forget analytics. Never awaited, never allowed to surface an error. */
export function recordEvent(token: string, event: string, position?: number): void {
  void fetch("/api/mini/events", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_token: token, event, position }),
    keepalive: true,
  }).catch(() => {
    /* analytics must never interrupt an assessment */
  });
}
